<template>
  <div class="mt-2">
    <WarningDisable permission="dump-credential" />
    <div class="flex gap-6">
      <div>
        <form
          class="mb-4 space-y-4"
          @submit="submit_without_reloading"
          @reset="resetFilter"
        >
          <div class="space-y-2">
            <div>
              <legend>Từ khóa</legend>
              <label class="input w-full md:w-fit 2xl:w-fit">
                <i
                  class="fa-solid fa-magnifying-glass text-base-content/80"
                ></i>
                <input
                  type="text"
                  placeholder="Tìm kiếm theo dữ liệu"
                  name="keyword"
                  v-model="keyword"
                />
              </label>
            </div>
            <div>
              <legend>Loại credential</legend>
              <label class="input w-full md:w-fit 2xl:w-fit">
                <i class="fa-solid fa-tag text-base-content/80"></i>
                <input
                  type="text"
                  placeholder="Lọc theo loại (tùy chọn)"
                  name="cred_type"
                  v-model="credType"
                />
              </label>
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <input
              class="btn btn-outline grow"
              type="reset"
              value="&#8635; Đặt lại"
              :disabled="loading"
            />
            <button
              class="btn btn-primary btn-outline grow"
              type="submit"
              :disabled="!filter_has_changed || loading"
            >
              &#10003; Áp dụng
            </button>
          </div>
        </form>
      </div>

      <div class="flex grow flex-col overflow-x-auto">
        <!-- Results -->
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <h2 class="card-title">
                <i class="fa-solid fa-list mr-2"></i>
                Kết quả Trích xuất mật khẩu
              </h2>
              <div class="flex gap-2">
                <button
                  class="btn btn-sm btn-outline"
                  @click="refreshResults"
                  :disabled="loading"
                >
                  <i class="fa-solid fa-refresh mr-1"></i>
                  Làm mới
                </button>
              </div>
            </div>

            <!-- Statistics -->
            <div
              v-if="results.length > 0"
              class="stats stats-horizontal mb-4 shadow"
            >
              <div class="stat">
                <div class="stat-title">Tổng kết quả</div>
                <div class="stat-value">{{ total_filter }}</div>
              </div>
            </div>

            <!-- Loading -->
            <Loading v-if="loading" />

            <!-- Results Table -->
            <div v-else-if="results.length > 0" class="overflow-x-auto">
              <table class="table-pin-rows table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Dữ liệu</th>
                    <th>Loại</th>
                    <th>Thời gian</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(result, idx) in results"
                    :key="result.id"
                    class="hover"
                  >
                    <th>{{ Number(idx) + 1 }}</th>
                    <td>
                      <div class="max-w-96 truncate" :title="result.data">
                        {{ result.data || "N/A" }}
                      </div>
                    </td>
                    <td>
                      <span class="badge badge-ghost badge-sm">{{
                        result.type || "N/A"
                      }}</span>
                    </td>
                    <td class="whitespace-nowrap">
                      {{ dayjs(result.created).fromNow() }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- No Results -->
            <div v-else class="alert alert-info">
              <i class="fa-solid fa-info-circle"></i>
              <span>Chưa có kết quả nào được tìm thấy.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { str_from_query } from "@/api";
import { listDumpCredentialCredentials } from "@/api/action";
import Loading from "@/components/LoadingTemplate.vue";
import dayjs from "dayjs";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import WarningDisable from "../WarningDisable.vue";

interface DumpCredentialItem {
  id: string;
  agent_id: string;
  scan_id: string;
  data: string;
  type: string;
  created: number;
  modified: number;
}

const route = useRoute();
const router = useRouter();
const action_id = route.params.action_id as string;
const client_id = computed(() => String(route.params.client_id));

const loading = ref(false);
const results = ref<DumpCredentialItem[]>([]);
const total_filter = ref(0);

// Filter state
const init_keyword = computed(() => str_from_query("keyword"));
const keyword = ref<string | undefined>(init_keyword.value);
const credType = ref<string | undefined>(undefined);
const filter_has_changed = computed(
  () => keyword.value !== init_keyword.value || credType.value !== undefined,
);

onMounted(() => {
  loadResults();
});

const loadResults = async () => {
  try {
    loading.value = true;

    const response = await listDumpCredentialCredentials(
      client_id.value,
      action_id,
      keyword.value || "",
      credType.value || "",
      1,
      100,
    );

    results.value = response.data || [];
    total_filter.value = response.total_filter;
  } catch (error) {
    console.error("Error loading results:", error);
    toast.error("Không thể tải kết quả Trích xuất mật khẩu!");
  } finally {
    loading.value = false;
  }
};

const refreshResults = () => {
  loadResults();
  toast.success("Đã làm mới kết quả!");
};

const submit_without_reloading = (e: Event) => {
  e.preventDefault();
  router.push({
    name: router.currentRoute.value.name,
    query: {
      ...router.currentRoute.value.query,
      page: 1,
      keyword: keyword.value,
      cred_type: credType.value,
    },
  });
};

const resetFilter = () => {
  keyword.value = undefined;
  credType.value = undefined;
  router.push({
    name: router.currentRoute.value.name,
    query: {
      page: 1,
    },
  });
};
</script>
